
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCounties } from "@/hooks/use-counties";
import { useCountyData } from "@/hooks/use-county-data";
import { useNursingHomes } from "@/hooks/use-nursing-homes";

export function useCountyPageData(countySlug: string | undefined) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("name_asc");
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);
  
  // Reset selected county when navigating to a new county page
  useEffect(() => {
    setSelectedCounty(null);
  }, [location.pathname]);
  
  // Fetch counties for the dropdown
  const { data: counties, isLoading: countiesLoading } = useCounties();
  
  // Fetch specific county data
  const { data: countyData, isLoading: countyDataLoading } = useCountyData(countySlug);
  
  // Fetch nursing homes based on current filters
  const { data: nursingHomes, isLoading, error } = useNursingHomes(
    selectedCounty || countySlug, 
    searchQuery, 
    sortBy
  );

  // Update the URL if the selected county changes through the dropdown
  useEffect(() => {
    if (selectedCounty && selectedCounty !== countySlug) {
      navigate(`/nursing-homes/${selectedCounty}`);
    }
  }, [selectedCounty, navigate, countySlug]);

  // Format county name from slug
  const formatCountyName = (slug?: string) => {
    if (!slug) return '';
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  const countyName = countyData?.name || formatCountyName(countySlug);
  
  // Format counties for dropdown
  const countyOptions = counties?.map(county => ({
    county: county.name,
    county_slug: county.slug
  })) || [];

  // Find nearby counties for cross-linking
  const nearbyCounties = counties?.filter(county => 
    county.slug !== countySlug
  ).slice(0, 4) || [];

  return {
    countyName,
    nursingHomes,
    counties,
    countyData,
    nearbyCounties,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    selectedCounty,
    setSelectedCounty,
    isLoading,
    error,
    countiesLoading,
    countyOptions
  };
}
